/**
 * Aba "Questionário Geral" da ficha: o questionário da cliente, editável também pela consultora.
 *
 * Porte de `portal/admin.html:2296-2480` (formulário), `4819-4886` (preencher, filhos, máscara do
 * telefone e salvar) e `5020-5028` (leitura). Linhas do inventário: 188 a 191.
 *
 * Território da frente A2. A moldura (`../index.tsx`) importa esta aba pelo nome `AbaQuestionario`.
 */

// Estado e retorno estável do React, e o tipo do envio do formulário.
import { useCallback, useState, type FormEvent } from 'react';
// As peças do Chassi para campo, texto longo, escolha e botão.
import { Botao, Campo, CampoTexto, Selecao } from '@complexity/ui';
// A leitura e a gravação da aba, no escopo da ficha.
import { lerQuestionarioGeral, salvarQuestionarioGeral, type LinhaGeralDaFicha } from '@studiolu/dados/areas/ficha';
// A fonte de dados.
import { usePainel } from '../../../contexto.js';
// O status que some sozinho e o grupo de escolha única, peças comuns do admin (A5).
import { LinhaDeStatus, useStatusTemporario } from '../../../comum/status-temporario.js';
import { GrupoRadio } from '../../../comum/grupo-radio.js';
// As colunas do questionário e as opções da pergunta 23, da cópia única.
import { OPCOES_Q23, PERGUNTAS_GERAL } from '../constantes.js';
// As caixas de marcar e as regras de leitura do legado.
import { GrupoDeMarcacao, alternarMarcado, opcoesDe } from '../comum-ficha/Marcacoes.js';
import { contem, mascararTelefoneDigitado } from '../comum-ficha/regras.js';
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
// O contrato de carga: a moldura lê esta aba antes de mostrá-la e relê a cada toque no botão dela.
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// O aviso de aba sem ficha, o escopo estável e o recorte por papel, comuns às abas (A3).
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';

/** Um campo do formulário, na ordem do HTML do legado, com o rótulo EXATO do formulário. */
type DefinicaoDeCampo =
  | {
      readonly tipo: 'texto';
      readonly coluna: string;
      readonly rotulo: string;
      // Atributos do `<input>` do legado que mudam o teclado ou o preenchimento automático.
      readonly atributos?: Readonly<Record<string, string | number>>;
    }
  | { readonly tipo: 'longo'; readonly coluna: string; readonly rotulo: string }
  | { readonly tipo: 'escolha'; readonly coluna: string; readonly rotulo: string; readonly opcoes: readonly string[] }
  | { readonly tipo: 'filhos' }
  | { readonly tipo: 'marcar'; readonly coluna: string; readonly rotulo: string; readonly opcoes: readonly string[] }
  | { readonly tipo: 'tamanhos' };

// As opções de tamanho de roupa, iguais nos três selects (`admin.html:2447-2466`).
const TAMANHOS_ROUPA = ['PP', 'P', 'M', 'G', 'GG', 'XG'];
// As opções de sapato, de 33 a 43 (`admin.html:2470-2474`).
const TAMANHOS_SAPATO = ['33', '34', '35', '36', '37', '38', '39', '40', '41', '42', '43'];
// A 25, "O que mais pesa hoje no guarda-roupa", com as dez opções exatas (`admin.html:2424-2435`).
const OPCOES_25 = [
  'Guarda-roupa sem graça, parece sempre igual',
  'Roupas desatualizadas',
  'Peças não cabem mais como antes',
  'Dificuldade em combinar as peças entre si',
  'Não gosto mais do que tenho',
  'Não sei o que realça meu corpo',
  'Falta roupa adequada pro trabalho',
  'Falta roupa pra sair à noite',
  'Tenho coisas que nunca uso',
  'Tenho muita roupa e fico perdida na hora de escolher',
];
// O complemento das perguntas de marcar várias.
const QUANTAS_QUISER = '(marque quantas quiser)';

/** Os seis blocos do formulário, com o título de cada um e os campos na ordem do HTML. */
const BLOCOS: readonly { readonly titulo: string; readonly campos: readonly DefinicaoDeCampo[] }[] = [
  {
    titulo: 'Dados pessoais',
    campos: [
      { tipo: 'texto', coluna: 'nome_completo', rotulo: '1. Nome completo', atributos: { autoComplete: 'name' } },
      { tipo: 'texto', coluna: 'endereco', rotulo: '2. Endereço', atributos: { autoComplete: 'street-address' } },
      {
        tipo: 'texto',
        coluna: 'telefone',
        rotulo: '3. Telefone',
        atributos: { type: 'tel', autoComplete: 'tel', inputMode: 'tel', placeholder: '(00) 00000-0000', maxLength: 15 },
      },
      { tipo: 'texto', coluna: 'email', rotulo: '4. E-mail', atributos: { type: 'email', autoComplete: 'email' } },
      { tipo: 'escolha', coluna: 'estado_civil', rotulo: '5. Estado civil', opcoes: ['Casada', 'Solteira', 'Outro'] },
      { tipo: 'filhos' },
    ],
  },
  {
    titulo: 'Rotina e dia a dia',
    campos: [
      {
        tipo: 'longo',
        coluna: 'profissao_rotina',
        rotulo: '7. Sua profissão e como é seu dia a dia (se movimenta mais fora ou trabalha em escritório?)',
      },
      {
        tipo: 'escolha',
        coluna: 'traje_recomendado_trabalho',
        rotulo: '8. Existe traje recomendado no trabalho?',
        opcoes: ['Sim', 'Não', 'Eventualmente'],
      },
      {
        tipo: 'longo',
        coluna: 'rotina_fins_de_semana',
        rotulo: '9. Nos fins de semana, costuma viajar? Pra onde e com que frequência?',
      },
      { tipo: 'texto', coluna: 'vida_noturna', rotulo: '10. Costuma sair à noite? Que tipo de lugar frequenta?' },
      { tipo: 'texto', coluna: 'uso_maquiagem', rotulo: '11. Usa maquiagem diariamente? O que costuma usar?' },
      {
        tipo: 'longo',
        coluna: 'rotina_diaria',
        rotulo: '12. Como é sua rotina diária: quanto tempo leva pra se arrumar, e quais atividades extras você tem?',
      },
    ],
  },
  {
    titulo: 'Cores, tecidos e acessórios',
    campos: [
      { tipo: 'texto', coluna: 'cores_preferidas', rotulo: '13a. Cores que mais usa e gosta' },
      { tipo: 'texto', coluna: 'cores_evitadas', rotulo: '13b. Cores que evita' },
      { tipo: 'texto', coluna: 'gosta_estampas', rotulo: '14a. Gosta de estampas? De que tipo?' },
      { tipo: 'texto', coluna: 'tecidos_preferidos', rotulo: '14b. Tecidos que prefere' },
      { tipo: 'texto', coluna: 'tecidos_evitados', rotulo: '14c. Tecidos que evita' },
      {
        tipo: 'texto',
        coluna: 'acessorios_preferencia',
        rotulo: '15. Acessórios: usa mais colar, pulseira, anel? Tamanho preferido?',
      },
    ],
  },
  {
    titulo: 'Seu guarda-roupa hoje',
    campos: [
      { tipo: 'texto', coluna: 'peca_favorita', rotulo: '16a. Peça favorita do guarda-roupa' },
      { tipo: 'texto', coluna: 'peca_que_evita', rotulo: '16b. Peça ou formato que não tem interesse em usar' },
      { tipo: 'texto', coluna: 'estilo_admirado', rotulo: '17. Alguma pessoa cuja forma de se vestir admira?' },
      { tipo: 'texto', coluna: 'locais_compra', rotulo: '18. Onde costuma comprar roupas, acessórios e sapatos?' },
      { tipo: 'texto', coluna: 'percepcao_preco', rotulo: '19. O que considera caro, barato ou justo por uma peça?' },
      { tipo: 'texto', coluna: 'marcas_preferidas', rotulo: '20. Marcas preferidas' },
    ],
  },
  {
    titulo: 'Como você se vê',
    campos: [
      { tipo: 'texto', coluna: 'parte_corpo_valorizada', rotulo: '21. Parte do corpo que mais gosta e valoriza' },
      { tipo: 'longo', coluna: 'dores_imagem_atual', rotulo: '22. Quais são as dores com a imagem hoje?' },
      {
        tipo: 'marcar',
        coluna: 'imagem_desejada',
        rotulo: '23a. Quero passar a imagem de uma pessoa',
        opcoes: OPCOES_Q23['imagem_desejada']!,
      },
      { tipo: 'marcar', coluna: 'sensacao_desejada', rotulo: '23b. Como quer se sentir', opcoes: OPCOES_Q23['sensacao_desejada']! },
      {
        tipo: 'marcar',
        coluna: 'objetivos_momento',
        rotulo: '23c. O que motiva a buscar essa consultoria agora',
        opcoes: OPCOES_Q23['objetivos_momento']!,
      },
      { tipo: 'longo', coluna: 'obstaculos_vestir', rotulo: '24. O que impede de se vestir como gostaria?' },
      { tipo: 'marcar', coluna: 'dificuldades_atuais', rotulo: '25. O que mais pesa hoje no guarda-roupa', opcoes: OPCOES_25 },
    ],
  },
  {
    titulo: 'Expectativas e tamanhos',
    campos: [
      { tipo: 'longo', coluna: 'expectativa_consultoria', rotulo: '26. Expectativa com a consultoria de imagem' },
      { tipo: 'texto', coluna: 'orcamento_compras', rotulo: '27. Pretende comprar peças novas? Qual orçamento disponível?' },
      { tipo: 'tamanhos' },
    ],
  },
];

/** Os quatro tamanhos, lado a lado na linha compacta do legado (`admin.html:2443-2476`). */
const TAMANHOS: readonly { readonly coluna: string; readonly rotulo: string; readonly opcoes: readonly string[] }[] = [
  { coluna: 'tamanho_cima', rotulo: '28a. Tamanho: parte de cima', opcoes: TAMANHOS_ROUPA },
  { coluna: 'tamanho_baixo', rotulo: '28b. Tamanho: parte de baixo', opcoes: TAMANHOS_ROUPA },
  { coluna: 'tamanho_lingerie', rotulo: '28c. Tamanho: lingerie', opcoes: TAMANHOS_ROUPA },
  { coluna: 'tamanho_sapato', rotulo: '28d. Tamanho: sapato', opcoes: TAMANHOS_SAPATO },
];

// As opções de cada select por coluna, para a leitura saber quais valores o select aceita.
const OPCOES_DE_SELECT: Readonly<Record<string, readonly string[]>> = Object.fromEntries([
  ...BLOCOS.flatMap((b) => b.campos).flatMap((c) => (c.tipo === 'escolha' ? [[c.coluna, c.opcoes] as const] : [])),
  ...TAMANHOS.map((t) => [t.coluna, t.opcoes] as const),
]);

// As quatro perguntas de marcar e as opções de cada uma, na ordem da tela.
const OPCOES_DE_MARCAR: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
  BLOCOS.flatMap((b) => b.campos).flatMap((c) => (c.tipo === 'marcar' ? [[c.coluna, c.opcoes] as const] : [])),
);

/** O que a tela guarda enquanto a pessoa edita. */
interface Formulario {
  // As colunas de texto e de escolha, pelo nome da coluna.
  readonly campos: Readonly<Record<string, string>>;
  // "Tem filhos?" e o complemento.
  readonly temFilhos: 'Não' | 'Sim';
  readonly qtdFilhos: string;
  // As marcadas de cada pergunta de marcar, na ordem das opções.
  readonly marcadas: Readonly<Record<string, readonly string[]>>;
}

/**
 * Preenche o formulário com a linha lida (`preencherFormularioGeral`, `admin.html:4819-4837`).
 *
 * Um detalhe do legado fica reproduzido: atribuir a um `<select>` um valor que não está nas opções
 * deixa o select VAZIO, e o próximo salvar grava vazio. Aqui o valor fora das opções vira vazio.
 */
function preencher(linha: LinhaGeralDaFicha | null): Formulario {
  const campos: Record<string, string> = {};
  for (const coluna of Object.keys(PERGUNTAS_GERAL)) {
    // Filhos e as perguntas de marcar têm tratamento próprio, logo abaixo.
    if (coluna === 'tem_filhos' || coluna in OPCOES_DE_MARCAR) continue;
    // `data?.[coluna] ?? ''`, e o valor vira texto como no `campo.value` do legado.
    const bruto = (linha as Record<string, unknown> | null)?.[coluna];
    const valor = bruto === null || bruto === undefined ? '' : String(bruto);
    const opcoes = OPCOES_DE_SELECT[coluna];
    campos[coluna] = opcoes && !opcoes.includes(valor) ? '' : valor;
  }
  // "Sim" ou "Sim - texto" marcam Sim e devolvem o texto; o resto marca Não (`4826-4830`).
  const temFilhos = linha?.tem_filhos || '';
  const ehSim = temFilhos.startsWith('Sim');
  const marcadas: Record<string, readonly string[]> = {};
  for (const [coluna, opcoes] of Object.entries(OPCOES_DE_MARCAR)) {
    // O `includes` do legado, em lista ou em texto (ver `contem`).
    marcadas[coluna] = opcoes.filter((o) => contem(linha?.[coluna as keyof LinhaGeralDaFicha] as string | readonly string[] | null, o));
  }
  return {
    campos,
    temFilhos: ehSim ? 'Sim' : 'Não',
    qtdFilhos: ehSim ? temFilhos.replace(/^Sim( - )?/, '') : '',
    marcadas,
  };
}

/** Monta o registro que o salvar grava (`admin.html:4860-4879`). */
function montarRegistro(f: Formulario): Record<string, unknown> {
  const registro: Record<string, unknown> = {};
  // Toda coluna com campo na tela, com o valor como está (texto, vazio incluso).
  for (const coluna of Object.keys(PERGUNTAS_GERAL)) {
    if (coluna === 'tem_filhos' || coluna in OPCOES_DE_MARCAR) continue;
    registro[coluna] = f.campos[coluna] ?? '';
  }
  // "Sim - quantidade", "Sim" sem texto, ou "Não".
  if (f.temFilhos === 'Sim') {
    const qtd = f.qtdFilhos.trim();
    registro['tem_filhos'] = qtd ? `Sim - ${qtd}` : 'Sim';
  } else {
    registro['tem_filhos'] = 'Não';
  }
  // As listas das perguntas de marcar, na ordem da tela.
  for (const coluna of Object.keys(OPCOES_DE_MARCAR)) registro[coluna] = [...(f.marcadas[coluna] ?? [])];
  return registro;
}

export function AbaQuestionario({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nem grava nada.
  if (!ficha) return <AbaSemFicha aba="Questionário Geral" />;
  return <FormularioGeral ficha={ficha} />;
}

function FormularioGeral({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const escopo = useEscopoEstavel(ficha);
  const { somenteLeitura } = useRecortePorPapel();
  // O formulário nasce vazio, como o `preencherFormularioGeral(null)` da abertura (`5951`).
  const [form, setForm] = useState<Formulario>(() => preencher(null));
  // O salvar fica travado até a leitura voltar (`admin.html:5961-5963` e `5053-5055`).
  const [carregado, setCarregado] = useState(false);
  // O salvar também trava enquanto grava (`botao.disabled = true`, `4858`).
  const [salvando, setSalvando] = useState(false);
  const [status, mostrar, fixar] = useStatusTemporario();

  // A leitura da aba (`carregarQuestionarios`, `admin.html:5023-5028`).
  const carregar = useCallback(async () => {
    const r = await lerQuestionarioGeral(dados, escopo);
    if (!r.ok) console.error('Erro ao carregar questionário geral:', r.erro);
    setForm(preencher(r.ok ? r.dados : null));
    setCarregado(true);
  }, [dados, escopo]);

  /*
   * A leitura pelo contrato da moldura, e não por um efeito próprio: ela roda ANTES de a aba
   * aparecer e de novo a cada toque no botão (`CARREGADOR_ABA_CLIENTE`, `admin.html:5086-5091`).
   * O que a pessoa digitou e não salvou volta ao que está no banco, como `preencherFormularioGeral`
   * faz no legado (`4819-4837`); a linha de status NÃO é tocada, e por isso ela sobrevive à troca
   * de aba, também como no legado.
   */
  useCarregadorDaAba(carregar);

  // O salvar (`admin.html:4854-4886`): também dispara no Enter de um campo, porque é um formulário.
  const salvar = async (evento: FormEvent) => {
    evento.preventDefault();
    setSalvando(true);
    fixar('Salvando...');
    const r = await salvarQuestionarioGeral(dados, escopo, montarRegistro(form));
    setSalvando(false);
    if (!r.ok) {
      fixar(`Erro ao salvar: ${r.erro.mensagem}`);
      return;
    }
    mostrar('Salvo!');
    // O legado relê os questionários depois de gravar, e o formulário volta com o que ficou no banco.
    await carregar();
  };

  // Troca o valor de uma coluna de texto ou de escolha.
  const definir = (coluna: string, valor: string) => setForm((f) => ({ ...f, campos: { ...f.campos, [coluna]: valor } }));

  // Desenha um campo pela definição.
  const desenhar = (c: DefinicaoDeCampo) => {
    switch (c.tipo) {
      case 'texto':
        return (
          <Campo
            key={c.coluna}
            rotulo={c.rotulo}
            value={form.campos[c.coluna] ?? ''}
            {...c.atributos}
            // A máscara do telefone é só deste campo, e acontece a cada tecla (`admin.html:4847-4853`).
            onChange={(e) =>
              definir(c.coluna, c.coluna === 'telefone' ? mascararTelefoneDigitado(e.target.value) : e.target.value)
            }
          />
        );
      case 'longo':
        return (
          <CampoTexto
            key={c.coluna}
            rotulo={c.rotulo}
            linhas={3}
            value={form.campos[c.coluna] ?? ''}
            onChange={(e) => definir(c.coluna, e.target.value)}
          />
        );
      case 'escolha':
        return (
          <Selecao
            key={c.coluna}
            rotulo={c.rotulo}
            vazio="Selecione"
            opcoes={opcoesDe(c.opcoes)}
            value={form.campos[c.coluna] ?? ''}
            disabled={somenteLeitura}
            onChange={(e) => definir(c.coluna, e.target.value)}
          />
        );
      case 'filhos':
        return (
          <div key="tem_filhos" className="flex flex-col gap-cx-1">
            <span className="text-cx-sm font-medium text-texto">
              6. Tem filhos?
            </span>
            <GrupoRadio
              nome="geral_tem_filhos"
              rotuloDoGrupo="6. Tem filhos?"
              opcoes={[
                { valor: 'Não', rotulo: 'Não' },
                { valor: 'Sim', rotulo: 'Sim' },
              ]}
              valor={form.temFilhos}
              desabilitado={somenteLeitura}
              aoMudar={(v) => setForm((f) => ({ ...f, temFilhos: v === 'Sim' ? 'Sim' : 'Não' }))}
            />
            {/* "Quantos? E as idades?" só aparece com Sim (`admin.html:2322-2325` e `4839-4844`). */}
            {form.temFilhos === 'Sim' && (
              <Campo
                rotulo="Quantos? E as idades?"
                placeholder="ex: 2 (5 e 9 anos)"
                value={form.qtdFilhos}
                onChange={(e) => {
                  const valor = e.target.value;
                  setForm((f) => ({ ...f, qtdFilhos: valor }));
                }}
              />
            )}
          </div>
        );
      case 'marcar':
        return (
          <GrupoDeMarcacao
            key={c.coluna}
            titulo={c.rotulo}
            complemento={QUANTAS_QUISER}
            opcoes={opcoesDe(c.opcoes)}
            marcadas={(v) => (form.marcadas[c.coluna] ?? []).includes(v)}
            aoMudar={(v, marcada) =>
              setForm((f) => ({
                ...f,
                marcadas: { ...f.marcadas, [c.coluna]: alternarMarcado(f.marcadas[c.coluna] ?? [], c.opcoes, v, marcada) },
              }))
            }
            desabilitado={somenteLeitura}
          />
        );
      case 'tamanhos':
        return (
          // A linha compacta: os quatro lado a lado, quebrando no celular.
          <div key="tamanhos" className="flex flex-wrap items-end gap-cx-4">
            {TAMANHOS.map((t) => (
              <div key={t.coluna} className="min-w-[10rem]">
                <Selecao
                  rotulo={t.rotulo}
                  vazio="Selecione"
                  opcoes={opcoesDe(t.opcoes)}
                  value={form.campos[t.coluna] ?? ''}
                  disabled={somenteLeitura}
                  onChange={(e) => definir(t.coluna, e.target.value)}
                />
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col gap-cx-4">
      <h3 className="text-cx-lg font-semibold text-texto">Questionário Geral</h3>
      <form onSubmit={(e) => void salvar(e)} className="flex flex-col gap-cx-6">
        {/* "Apenas Consulta" tem todo campo desabilitado (`admin.html:3393`): o `fieldset` desliga
            os campos de dentro de uma vez, inclusive os que nascem depois. */}
        <fieldset disabled={somenteLeitura} className="m-0 flex min-w-0 flex-col gap-cx-6 border-0 p-0">
          {BLOCOS.map((bloco) => (
            <section key={bloco.titulo} className="flex flex-col gap-cx-4">
              <h4 className="border-b border-borda pb-cx-2 text-cx-base font-semibold text-texto">{bloco.titulo}</h4>
              {bloco.campos.map(desenhar)}
            </section>
          ))}
        </fieldset>
        {!somenteLeitura && (
          <Botao type="submit" className="self-start" disabled={!carregado || salvando}>
            Salvar / reenviar questionário
          </Botao>
        )}
        <LinhaDeStatus texto={status} testid="status-geral" />
      </form>
    </div>
  );
}
